import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Menu,
  MenuItem,
  ListSubheader,
  ListItemIcon,
  Divider,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AccountCircle,
  Logout,
  Login,
  ExpandMore,
  AdminPanelSettings,
} from '@mui/icons-material';
import { useAuthStore } from '@/store';
import { useAdminMode, useCurrentUserLabel } from '@/hooks/useManage';
import { isPathActive, primaryNavItems, resolveMenuSections } from './navigation';

export const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { isAuthenticated, logout } = useAuthStore();
  const { adminMode, canManage, toggle, isSuperAdmin } = useAdminMode();
  const userLabel = useCurrentUserLabel();
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);
  const [userAnchor, setUserAnchor] = useState<null | HTMLElement>(null);

  const sections = resolveMenuSections({ isAuthenticated, showSports: adminMode && isSuperAdmin });
  const mineSection = sections.find((s) => s.id === 'mine');
  const moreSections = sections.filter((s) => s.id !== 'mine');

  const closeMenus = () => {
    setMoreAnchor(null);
    setUserAnchor(null);
  };

  const go = (path: string) => {
    closeMenus();
    navigate(path);
  };

  const handleLogout = () => {
    closeMenus();
    logout();
    navigate('/');
  };

  return (
    <AppBar position="static" sx={{ bgcolor: 'background.paper', color: 'text.primary', boxShadow: 1, pt: 'env(safe-area-inset-top)' }}>
      <Toolbar className="cont" sx={{ minHeight: 56, px: 1, gap: 0.5 }}>
        <Box
          component="button"
          type="button"
          onClick={() => go('/')}
          aria-label="4EMP — на главную"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            border: 0,
            bgcolor: 'transparent',
            color: 'inherit',
            cursor: 'pointer',
            p: 0,
            mr: 2,
            minHeight: 48,
          }}
        >
          <Box component="img" src="/logo-v4.png" alt="" sx={{ width: 28, height: 28, objectFit: 'contain' }} />
          <Box sx={{ fontWeight: 700, fontSize: '1rem' }}>4EMP</Box>
        </Box>

        {isDesktop && (
          <Box component="nav" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {primaryNavItems.map(({ id, path, label }) => (
              <Button
                key={id}
                color="inherit"
                onClick={() => go(path)}
                sx={{
                  minHeight: 40,
                  fontWeight: isPathActive(path, location.pathname) ? 700 : 500,
                  color: isPathActive(path, location.pathname) ? 'primary.main' : 'inherit',
                }}
              >
                {label}
              </Button>
            ))}
            <Button color="inherit" endIcon={<ExpandMore />} onClick={(e) => setMoreAnchor(e.currentTarget)}>
              Ещё
            </Button>
            <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={closeMenus}>
              {moreSections.flatMap((section) => [
                <ListSubheader key={`h-${section.id}`} sx={{ bgcolor: 'inherit', lineHeight: '32px' }}>
                  {section.title}
                </ListSubheader>,
                ...section.items.map(({ id, path, label, icon: Icon }) => (
                  <MenuItem key={id} onClick={() => go(path)}>
                    <ListItemIcon>
                      <Icon fontSize="small" />
                    </ListItemIcon>
                    {label}
                  </MenuItem>
                )),
              ])}
            </Menu>
          </Box>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {isDesktop && canManage && (
          <Button
            variant={adminMode ? 'contained' : 'outlined'}
            color="manage"
            startIcon={<AdminPanelSettings />}
            onClick={toggle}
            aria-pressed={adminMode}
          >
            Админ
          </Button>
        )}

        {isAuthenticated ? (
          <>
            <Button
              color="inherit"
              aria-label="Профиль"
              onClick={(e) => setUserAnchor(e.currentTarget)}
              endIcon={<AccountCircle />}
              sx={{ minHeight: 48, minWidth: 48, textTransform: 'none', fontWeight: 500, '& .MuiButton-endIcon': { ml: userLabel ? 1 : 0 } }}
            >
              {userLabel && (
                <Typography component="span" variant="body2" noWrap sx={{ maxWidth: { xs: '40vw', lg: 220 } }}>
                  {userLabel}
                </Typography>
              )}
            </Button>
            <Menu
              anchorEl={userAnchor}
              open={Boolean(userAnchor)}
              onClose={closeMenus}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={() => go('/profile')}>
                <ListItemIcon>
                  <AccountCircle fontSize="small" />
                </ListItemIcon>
                Мой профиль
              </MenuItem>
              {mineSection && <Divider />}
              {mineSection?.items.map(({ id, path, label, icon: Icon }) => (
                <MenuItem key={id} onClick={() => go(path)}>
                  <ListItemIcon>
                    <Icon fontSize="small" />
                  </ListItemIcon>
                  {label}
                </MenuItem>
              ))}
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                <ListItemIcon sx={{ color: 'error.main' }}>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Выйти
              </MenuItem>
            </Menu>
          </>
        ) : (
          <Button variant="contained" startIcon={<Login />} onClick={() => go('/login')} sx={{ minHeight: 40 }}>
            Войти
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
};
